import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { Menu } from "../../components/common";
import { ROUTES } from "../../routes";
import { isMobileMedia } from "../../utils/media";
import { selectCurrentUser } from "../../store/reducers/user";
import { AccountType } from "../../types/UserTypes";
import { Currency } from "../../types/InvoiceTypes";

enum Visibility {
  CONTRACTOR_ONLY,
  ONMETA_ONLY,
  ONMETA_CONTRACTOR_HIDE,
  MOBILE_HIDE,
  ONMETA_HIDE,
  USD_ONLY,
  USD_HIDE,
}

type MenuItem = {
  key: string;
  icon: string;
  name: string;
  to: string;
  visibility?: Visibility[];
};

const getMenuList = (
  isOnmetaEligible: boolean,
  accountType: AccountType,
  isUSDEligible: boolean
): MenuItem[] =>
  [
    // {
    //   key: "home",
    //   icon: "Home",
    //   name: "Home",
    //   to: ROUTES.HOME,
    //   visibility: [Visibility.ONMETA_ONLY, Visibility.CONTRACTOR_ONLY],
    // },
    {
      key: "transfers",
      icon: "Send",
      name: isOnmetaEligible ? "Personal transfers" : "Transfers",
      to: ROUTES.PAYMENT_REQUESTS,
      visibility: [Visibility.ONMETA_ONLY, Visibility.CONTRACTOR_ONLY],
    },
    {
      key: "receive",
      icon: "Receive",
      name: isOnmetaEligible ? "Business payments" : "Receive",
      to: ROUTES.RECEIVE,
    },
    {
      key: "pay",
      icon: "Pay",
      name: "Pay",
      to: ROUTES.PAY,
      visibility: [Visibility.ONMETA_CONTRACTOR_HIDE, Visibility.MOBILE_HIDE],
    },
    {
      key: "buy",
      icon: "USDC",
      name: "Buy USDC",
      to: ROUTES.BUY,
      visibility: [Visibility.ONMETA_HIDE, Visibility.USD_HIDE],
    },
    // {
    //   key: "deposit",
    //   icon: "Arrow-right",
    //   name: "My Deposits",
    //   to: ROUTES.DEPOSIT,
    //   visibility: [Visibility.ONMETA_ONLY],
    // },
    {
      key: "usd",
      icon: "Bank",
      name: "$ USD Account",
      to: ROUTES.USD_ACCOUNT,
      visibility: [Visibility.USD_ONLY],
    },
    // {
    //   key: "cashout",
    //   icon: "Payments",
    //   name: "Exchange USDT to INR",
    //   to: ROUTES.CASH_OUT,
    // },
    {
      key: "contacts",
      icon: "People",
      name: isOnmetaEligible ? "Your contractors" : "Contacts",
      to: ROUTES.CONTACTS,
    },
    {
      key: "wallets",
      icon: "Wallet",
      name: "Wallets",
      to: ROUTES.WALLETS,
    },
  ].filter(({ visibility }) => {
    if (!visibility) {
      return true;
    }

    if (
      isOnmetaEligible &&
      accountType === AccountType.BUSINESS &&
      isMobileMedia()
    ) {
      return (
        !visibility?.includes(Visibility.CONTRACTOR_ONLY) &&
        !visibility?.includes(Visibility.MOBILE_HIDE) &&
        !visibility?.includes(Visibility.ONMETA_HIDE)
      );
    }

    if (isOnmetaEligible && accountType === AccountType.BUSINESS) {
      return (
        !visibility?.includes(Visibility.CONTRACTOR_ONLY) &&
        !visibility?.includes(Visibility.ONMETA_HIDE)
      );
    }

    if (isOnmetaEligible) {
      return (
        !visibility?.includes(Visibility.ONMETA_CONTRACTOR_HIDE) &&
        !visibility?.includes(Visibility.ONMETA_HIDE)
      );
    }

    if (isUSDEligible) {
      return (
        !visibility?.includes(Visibility.ONMETA_ONLY) &&
        !visibility?.includes(Visibility.USD_HIDE)
      );
    }

    if (isMobileMedia()) {
      return (
        !visibility?.includes(Visibility.ONMETA_ONLY) &&
        !visibility?.includes(Visibility.MOBILE_HIDE) &&
        !visibility?.includes(Visibility.USD_ONLY)
      );
    }

    return (
      !visibility?.includes(Visibility.ONMETA_ONLY) &&
      !visibility?.includes(Visibility.USD_ONLY)
    );
  });

type Props = {
  onClick?: () => void;
};

export const DashboardMenu: FC<Props> = ({ onClick }) => {
  const location = useLocation();
  const { is_onmeta_eligible, account_type, allowed_incoming_currencies } =
    useSelector(selectCurrentUser);
  const isUSDEligible = allowed_incoming_currencies.includes(Currency.USD);

  const menuList = useMemo(
    () => getMenuList(is_onmeta_eligible, account_type, isUSDEligible),
    [is_onmeta_eligible, account_type, isUSDEligible]
  );

  return (
    <Menu
      list={menuList}
      active={location.pathname}
      inverse
      onClickMenuItem={onClick}
    />
  );
};
